import type {
	FunctionsFetchError,
	FunctionsHttpError,
	FunctionsRelayError,
} from '@supabase/functions-js';
import type { AuthError } from '@supabase/supabase-js';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { supabase } from '@/shared/api';
import { userKeys } from '../api/user.keys';

type FunctionsError =
	| FunctionsHttpError
	| FunctionsRelayError
	| FunctionsFetchError;

type AuthUser = {
	id: string;
	email?: string;
	created_at?: string;
	last_sign_in_at?: string;
	user_metadata?: {
		avatar_url?: string;
		displayName?: string;
		display_name?: string;
		name?: string;
	};
};

type UseAuthResult = {
	isLoading: boolean;
	isLoggedIn: boolean;
	userId: string | undefined;
	email: string | undefined;
	createdAt: string | undefined;
	lastSignInAt: string | undefined;
	avatarUrl: string | undefined;
	displayName: string | undefined;
	logout: (
		onSuccess?: () => void,
		onError?: (e?: AuthError) => void,
	) => Promise<void>;
	deleteUser: (
		onSuccess?: () => void,
		onError?: (e?: FunctionsError) => void,
	) => Promise<void>;
};

const getDisplayName = (user: AuthUser | null) =>
	user?.user_metadata?.displayName ??
	user?.user_metadata?.display_name ??
	user?.user_metadata?.name;

const useAuth = (): UseAuthResult => {
	const queryClient = useQueryClient();
	const [user, setUser] = useState<AuthUser | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			setUser((session?.user as AuthUser | null) ?? null);
			setIsLoading(false);
		});

		return () => subscription.unsubscribe();
	}, []);

	const clearAuthState = () => {
		setUser(null);
		queryClient.removeQueries({ queryKey: userKeys.profile() });
	};

	const logout = async (
		onSuccess?: () => void,
		onError?: (e?: AuthError) => void,
	) => {
		const { error } = await supabase.auth.signOut();
		clearAuthState();

		if (error) {
			onError?.(error);
		} else {
			onSuccess?.();
		}
	};

	const deleteUser = async (
		onSuccess?: () => void,
		onError?: (e?: FunctionsError) => void,
	) => {
		const {
			data: { session },
		} = await supabase.auth.getSession();

		const { error } = await supabase.functions.invoke('delete-user', {
			headers: { Authorization: `Bearer ${session?.access_token}` },
		});

		if (error) {
			onError?.(error);
		} else {
			await logout();
			onSuccess?.();
		}
	};

	return {
		isLoading,
		isLoggedIn: !!user,
		userId: user?.id,
		email: user?.email,
		createdAt: user?.created_at,
		lastSignInAt: user?.last_sign_in_at,
		avatarUrl: user?.user_metadata?.avatar_url,
		displayName: getDisplayName(user),
		logout,
		deleteUser,
	};
};

export default useAuth;
